<script lang="ts">
	/**
	 * `/Settings/Currencies` — which currencies the store prices in.
	 *
	 * `minorUnitDigits` is the field worth caring about: it is data, not an assumption. Two for
	 * USD, **zero for JPY** — and a hard-coded two is what charges a zero-decimal currency a
	 * hundred times the price (Design §4.5).
	 *
	 * Disabling a currency stops *new* purchases in it; existing subscriptions keep billing in it,
	 * because the alternative is silently repricing a customer.
	 */

	import { onMount } from 'svelte';
	import { Badge, Button, Input, Table, TableBody, TableBodyCell, TableBodyRow, TableHead, TableHeadCell, Toggle } from 'flowbite-svelte';
	import { ConfirmButton, EmptyState, FormField, PageHeader, Panel, FormModal } from '../../components';
	import { createCurrency, listCurrencies, updateCurrency } from '../../AppNetwork';
	import type { Currency } from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';

	let currencies: Currency[] = [];
	let loading: boolean = true;
	let loadError: string = '';
	let busyCode: string = '';

	let isAdding: boolean = false;
	let creating: boolean = false;

	/**
	 * The currency being edited, addressed by its ISO code.
	 *
	 * The code itself is not editable: it is the primary key, and every price amount, invoice and
	 * subscription in the store references it. A currency spelled wrongly is retired and replaced,
	 * not renamed.
	 */
	let editingCode: string = '';
	let edit = { name: '', symbol: '', displayOrder: 0 };

	$: editing = currencies.find(currency => currency.code === editingCode) ?? null;
	$: canSaveEdit =
		busyCode === '' && edit.name.trim().length > 0 && edit.symbol.trim().length > 0;

	function fnStartEdit(currency: Currency): void {
		editingCode = currency.code;
		edit = {
			name: currency.name,
			symbol: currency.symbol,
			displayOrder: currency.displayOrder
		};
	}

	async function fnSaveEdit(): Promise<void> {
		if (editing === null) {
			return;
		}
		const code = editing.code;
		busyCode = code;
		try {
			const updated = await updateCurrency(code, {
				name: edit.name.trim(),
				symbol: edit.symbol.trim(),
				displayOrder: Number(edit.displayOrder)
			});
			currencies = currencies.map(currency => (currency.code === code ? updated : currency));
			toastSuccess(`${code.toUpperCase()} saved.`);
			editingCode = '';
		} catch (error) {
			toastError('The currency was not saved.', (error as Error).message);
		} finally {
			busyCode = '';
		}
	}
	let draft = { code: '', name: '', symbol: '', minorUnitDigits: 2, roundingIncrement: 1, displayOrder: 0 };

	$: codeError =
		draft.code.length === 0 || /^[A-Za-z]{3}$/.test(draft.code)
			? ''
			: 'A currency code is exactly three letters (ISO-4217).';
	$: canCreate =
		!creating &&
		/^[A-Za-z]{3}$/.test(draft.code) &&
		draft.name.trim().length > 0 &&
		draft.symbol.trim().length > 0 &&
		Number.isInteger(Number(draft.minorUnitDigits)) &&
		Number(draft.minorUnitDigits) >= 0 &&
		Number(draft.minorUnitDigits) <= 4;

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			currencies = (await listCurrencies()).items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnPatch(currency: Currency, changes: Partial<Currency>): Promise<void> {
		busyCode = currency.code;
		try {
			const updated = await updateCurrency(currency.code, changes);
			// A promote clears the previous default, so the whole list is re-read after one.
			if (changes.isDefault === true) {
				await fnLoad();
			} else {
				currencies = currencies.map(row => (row.code === updated.code ? updated : row));
			}
			toastSuccess(`${updated.code.toUpperCase()} saved.`);
		} catch (error) {
			// Re-read so a refused toggle snaps back to what the server actually holds.
			await fnLoad();
			toastError('The currency was not changed.', (error as Error).message);
		} finally {
			busyCode = '';
		}
	}

	async function fnCreate(): Promise<void> {
		creating = true;
		try {
			await createCurrency({
				code: draft.code.toLowerCase(),
				name: draft.name.trim(),
				symbol: draft.symbol.trim(),
				minorUnitDigits: Number(draft.minorUnitDigits),
				roundingIncrement: Number(draft.roundingIncrement),
				displayOrder: Number(draft.displayOrder)
			});
			toastSuccess(`${draft.code.toUpperCase()} added.`);
			draft = { code: '', name: '', symbol: '', minorUnitDigits: 2, roundingIncrement: 1, displayOrder: 0 };
			isAdding = false;
			await fnLoad();
		} catch (error) {
			toastError('The currency was not added.', (error as Error).message);
		} finally {
			creating = false;
		}
	}

	// Handlers live here rather than inline: Svelte's markup expressions are parsed as plain
	// JavaScript, so a TypeScript cast on `event.target` is a compile error in the template.
	function fnNumberFrom(event: Event): number {
		return Number((event.target as HTMLInputElement).value);
	}

	function fnCheckedFrom(event: Event): boolean {
		return (event.target as HTMLInputElement).checked;
	}

	/** `1234567` minor units rendered the way this currency's own settings say it should be. */
	function sample(currency: Currency): string {
		const amount = 1234567 / Math.pow(10, currency.minorUnitDigits);
		return `${currency.symbol}${amount.toFixed(currency.minorUnitDigits)}`;
	}

	onMount(fnLoad);
</script>

<PageHeader title="Currencies">
	<svelte:fragment slot="description">
		Which currencies the store prices in, which one is the default, and how each formats.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<strong>Minor unit digits</strong> is how many decimal places the currency has: two for USD,
		zero for JPY. It comes from this row rather than from an assumption, which is what keeps a
		zero-decimal currency from being charged a hundred times over.
	</svelte:fragment>
</PageHeader>

{#if loading}
	<EmptyState variant="loading" title="Loading currencies…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="Currencies could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else}
	<div class="space-y-4">
		<Panel title="Currencies" faIcon="coins">
			<svelte:fragment slot="titleRight">
				<Button size="sm" color="primary" on:click={() => (isAdding = !isAdding)}>
					<i class="fa-solid fa-plus mr-2" />Add currency
				</Button>
			</svelte:fragment>

			{#if currencies.length === 0}
				<EmptyState
					title="No currency is configured."
					description="The store stays closed until one currency is enabled and marked as the default."
				>
					<Button color="primary" size="sm" on:click={() => (isAdding = true)}>Add the first currency</Button>
				</EmptyState>
			{:else}
				<Table divClass="relative overflow-x-auto">
					<TableHead>
						<TableHeadCell>Code</TableHeadCell>
						<TableHeadCell>Name</TableHeadCell>
						<TableHeadCell>Format</TableHeadCell>
						<TableHeadCell>Minor units</TableHeadCell>
						<TableHeadCell>Rounding</TableHeadCell>
						<TableHeadCell>Enabled</TableHeadCell>
						<TableHeadCell>Default</TableHeadCell>
						<TableHeadCell />
					</TableHead>
					<TableBody>
						{#each currencies as currency (currency.code)}
							<TableBodyRow>
								<TableBodyCell class="dataTable font-mono uppercase">{currency.code}</TableBodyCell>
								<TableBodyCell class="dataTable">{currency.name}</TableBodyCell>
								<TableBodyCell class="dataTable">{sample(currency)}</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Input
										type="number"
										size="sm"
										min="0"
										max="4"
										step="1"
										class="w-20"
										disabled={busyCode === currency.code}
										value={currency.minorUnitDigits}
										on:change={event => fnPatch(currency, { minorUnitDigits: fnNumberFrom(event) })}
									/>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Input
										type="number"
										size="sm"
										min="1"
										step="1"
										class="w-24"
										disabled={busyCode === currency.code}
										value={currency.roundingIncrement}
										on:change={event => fnPatch(currency, { roundingIncrement: fnNumberFrom(event) })}
									/>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Toggle
										checked={currency.isEnabled}
										disabled={busyCode === currency.code}
										on:change={event => fnPatch(currency, { isEnabled: fnCheckedFrom(event) })}
									/>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if currency.isDefault}
										<Badge color="green">Default</Badge>
									{:else}
										<ConfirmButton
											size="xs"
											color="alternative"
											disabled={busyCode === currency.code || !currency.isEnabled}
											confirmText={`Make ${currency.code.toUpperCase()} the default currency? New purchases will be priced in it unless the customer picks another.`}
											on:confirm={() => fnPatch(currency, { isDefault: true })}
										>
											Make default
										</ConfirmButton>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Button size="xs" color="alternative" on:click={() => fnStartEdit(currency)}>
										<i class="fa-solid fa-pen mr-1" />Edit
									</Button>
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>

		{#if editing !== null}
			<FormModal
				open={true}
				title={`Edit ${editing.code.toUpperCase()}`}
				faIcon="pen"
				submitLabel="Save currency"
				size="md"
				busy={busyCode !== ''}
				canSubmit={canSaveEdit}
				on:submit={fnSaveEdit}
				on:cancel={() => (editingCode = '')}
			>
				<div class="grid md:grid-cols-2 gap-4">
					<FormField
						id="currency-edit-code"
						label="Code"
						description="ISO-4217, and the key every price, invoice and subscription references. Fixed once created."
					>
						<Input id="currency-edit-code" value={editing.code.toUpperCase()} readonly disabled class="font-mono" />
					</FormField>

					<FormField id="currency-edit-name" label="Name" required>
						<Input id="currency-edit-name" bind:value={edit.name} disabled={busyCode !== ''} />
					</FormField>

					<FormField
						id="currency-edit-symbol"
						label="Symbol"
						required
						description="Rendered before every amount in this currency, on screen and on the invoice."
					>
						<Input id="currency-edit-symbol" bind:value={edit.symbol} disabled={busyCode !== ''} />
					</FormField>

					<FormField
						id="currency-edit-order"
						label="Display order"
						description="Lower sorts first in the storefront selector."
					>
						<Input
							id="currency-edit-order"
							type="number"
							min="0"
							step="1"
							bind:value={edit.displayOrder}
							disabled={busyCode !== ''}
						/>
					</FormField>
				</div>
			</FormModal>
		{/if}

		<FormModal
			bind:open={isAdding}
			title="Add a currency"
			faIcon="plus"
			submitLabel="Add currency"
			size="md"
			busy={creating}
			canSubmit={canCreate}
			on:submit={fnCreate}
		>
					<div class="grid md:grid-cols-3 gap-4">
						<FormField id="currency-code" label="Code" required description="Three letters, ISO-4217." error={codeError}>
							<Input id="currency-code" bind:value={draft.code} placeholder="jpy" maxlength="3" disabled={creating} />
						</FormField>

						<FormField id="currency-name" label="Name" required>
							<Input id="currency-name" bind:value={draft.name} placeholder="Japanese Yen" disabled={creating} />
						</FormField>

						<FormField id="currency-symbol" label="Symbol" required>
							<Input id="currency-symbol" bind:value={draft.symbol} placeholder="¥" disabled={creating} />
						</FormField>

						<FormField
							id="currency-digits"
							label="Minor unit digits"
							required
							description="Decimal places. 2 for USD, 0 for JPY."
						>
							<Input id="currency-digits" type="number" min="0" max="4" step="1" bind:value={draft.minorUnitDigits} disabled={creating} />
						</FormField>

						<FormField
							id="currency-rounding"
							label="Rounding increment"
							description="Minor units a displayed price rounds to, so prices land on 9.99 rather than 10.03."
						>
							<Input id="currency-rounding" type="number" min="1" step="1" bind:value={draft.roundingIncrement} disabled={creating} />
						</FormField>

						<FormField id="currency-order" label="Display order" description="Lower sorts first in the storefront selector.">
							<Input id="currency-order" type="number" min="0" step="1" bind:value={draft.displayOrder} disabled={creating} />
						</FormField>
					</div>
		</FormModal>
	</div>
{/if}
